/**
 * The progression row of a change to one exercise under review (ai-plan-builder R15; board U): the
 * day form's statement, read-only, with the rule the exercise had struck before the one it gets
 * when the change moves it (`ruleChangeView` decides that).
 *
 * The statement names each rule's kind and field and never its increment (`ruleStatement`'s header
 * says why). The proposal does state one, „+2,5 kg / cykl", worded by the host from the proposal's
 * own line; it arrives here as text and stands after the chips.
 */

import type { ExerciseDraft } from '~/entities/plan-draft';
import visuallyHidden from '~/shared/ui/visuallyHidden.module.css';

import type { Translate } from '../model/matrixCellState';
import { ruleChangeView, type RuleIncrements } from '../model/ruleStatement';

import styles from './DayProgressionSlots.module.css';
import { StatementChips } from './StatementChips';

const KEY = 'web:planBuilder.progression';

export interface RuleChangeProps {
  /** The exercise as the change writes it. */
  readonly exercise: ExerciseDraft;
  /** The exercise it alters, to compare with; `null` where there is nothing to compare. */
  readonly previous: ExerciseDraft | null;
  readonly increments: RuleIncrements;
  readonly t: Translate;
}

export function RuleChange({ exercise, previous, increments, t }: RuleChangeProps) {
  const view = ruleChangeView(exercise, previous, increments, t);
  return (
    <div className={styles.row} data-change="">
      <span className={styles.label}>{t(`${KEY}.bar.title`)}</span>
      <span className={styles.change} aria-hidden="true">
        {view.was !== null && (
          <s className={styles.previous}>
            <StatementChips view={view.was} />
            {increments.before !== null && <span className={styles.axisField}>{increments.before}</span>}
          </s>
        )}
        <StatementChips view={view.now} />
        {increments.after !== null && <span className={styles.axisField}>{increments.after}</span>}
      </span>
      <span className={visuallyHidden.srOnly}>{view.spoken}</span>
    </div>
  );
}
